/**
 * windstreamPipeline — 200+ particles in a curl-noise flow field
 * with fading trails, color by speed, dynamic obstacles.
 *
 * Composition:
 *   1. Background — dark gradient with subtle FBM turbulence.
 *   2. Obstacles — 4 circular obstacles at varying positions that
 *      redirect the flow around themselves.
 *   3. Particle trails — fading line segments showing the recent
 *      path of each particle (the trails themselves act as the
 *      visualization).
 *   4. Particles — bright dots at the head of each trail, colored
 *      by speed (red = slow, blue = fast).
 *   5. Speed overlay — sparse heat-map tinting near obstacles.
 *
 * Audio reactivity:
 *   bass — boosts flow speed, scales obstacle repulsion
 *   mid  — moves obstacles around, modulates flow direction
 *   high — flickers the particle heads, intensifies trail color
 *
 * Each particle is integrated in the shader by hashing an id into
 * its initial position and stepping via curl-noise velocity.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface WindstreamPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float h11(float n) {
  return fract(sin(n * 91.345) * 47453.12);
}

float h22(vec2 p) {
  p = fract(p * vec2(91.83, 47.31));
  p += dot(p, p + 23.71);
  return fract(p.x * p.y);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float r = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    r += a * noise2(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return r;
}

// Curl noise — derivative of FBM for divergence-free flow.
vec2 curl(vec2 p) {
  float eps = 0.01;
  float n1 = fbm(p + vec2(0.0, eps));
  float n2 = fbm(p - vec2(0.0, eps));
  float n3 = fbm(p + vec2(eps, 0.0));
  float n4 = fbm(p - vec2(eps, 0.0));
  return vec2(n1 - n2, -(n3 - n4)) / (2.0 * eps);
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // --- 1. Background ---
  float bgT = clamp((p.y + 0.8) / 1.6, 0.0, 1.0);
  vec3 bg = mix(vec3(0.02, 0.01, 0.05), vec3(0.04, 0.04, 0.10), bgT);
  bg += vec3(0.20, 0.10, 0.30) * fbm(p * 1.5 + vec2(uTime * 0.03, uTime * 0.02)) * 0.20;

  vec3 col = bg;

  // --- 2. Obstacles (4 circular) ---
  vec2 obstPos[4];
  float obstR[4];
  obstPos[0] = vec2(-0.30, 0.20);
  obstPos[1] = vec2(0.25, -0.15);
  obstPos[2] = vec2(-0.10, -0.30);
  obstPos[3] = vec2(0.40, 0.30);
  obstR[0] = 0.08;
  obstR[1] = 0.10;
  obstR[2] = 0.07;
  obstR[3] = 0.09;

  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    // Animate obstacles.
    vec2 c = obstPos[i] + vec2(
      sin(uTime * (0.3 + fi * 0.1) + fi) * 0.10,
      cos(uTime * (0.4 + fi * 0.07) + fi * 1.3) * 0.10
    ) * (0.4 + mid * 0.6);
    float radius = obstR[i] * (0.8 + 0.2 * sin(uTime * 1.5 + fi * 1.7)) * (0.7 + bass * 0.6);
    float d = length(p - c);
    // Solid obstacle body.
    if (d < radius) {
      float bodyT = smoothstep(radius, radius * 0.9, d);
      col = mix(col, vec3(0.10, 0.08, 0.20), bodyT);
      // Rim.
      float rim = exp(-pow(d - radius, 2.0) * 600.0);
      col += vec3(0.4, 0.3, 0.6) * rim * 0.6;
    }
    // Influence glow halo.
    float halo = exp(-max(0.0, d - radius) * 8.0) * 0.4;
    col += vec3(0.3, 0.2, 0.5) * halo * (0.3 + mid * 0.4);
  }

  // --- 3. Particle trails & heads ---
  // Each particle is integrated by stepping along curl-noise velocity.
  // We trace backwards N steps from the current position.
  const int NUM_PARTICLES = 220;
  const int TRAIL_STEPS = 18;

  float trailAccum = 0.0;
  vec3 trailCol = vec3(0.0);
  vec3 headCol = vec3(0.0);

  for (int pi = 0; pi < 220; pi++) {
    float fpi = float(pi);
    // Each particle has a stable seed.
    float seed = h11(fpi * 1.93 + 4.7);
    float seed2 = h11(fpi * 2.71 + 11.3);
    float speed = 0.05 + 0.08 * (0.5 + bass * 0.5);

    // Initial position of particle in [-1, 1] range, with mod for wrapping.
    vec2 initPos = vec2(
      (h11(fpi * 3.13) - 0.5) * 2.0,
      (h11(fpi * 5.27) - 0.5) * 2.0
    );

    // Simulate particle position. We compute it deterministically.
    // Phase shifts every cycle.
    float cycle = mod(uTime * speed + seed * 10.0, 8.0);
    vec2 pos = initPos;

    // Step forward from t=0 to t=cycle using curl noise.
    float t = 0.0;
    vec2 vel = vec2(0.0);
    for (int s = 0; s < 18; s++) {
      if (float(s) > cycle / 0.05) break;
      t = float(s) * 0.05;
      // Get curl noise at this position with time offset.
      vec2 cn = curl(pos * 1.5 + vec2(uTime * 0.05, uTime * 0.03));
      vel = cn * (0.5 + bass * 0.8);

      // Obstacle repulsion.
      for (int oi = 0; oi < 4; oi++) {
        float foi = float(oi);
        vec2 oc = obstPos[oi] + vec2(
          sin(uTime * (0.3 + foi * 0.1) + foi) * 0.10,
          cos(uTime * (0.4 + foi * 0.07) + foi * 1.3) * 0.10
        ) * (0.4 + mid * 0.6);
        float orad = obstR[oi] * (0.8 + 0.2 * sin(uTime * 1.5 + foi * 1.7)) * (0.7 + bass * 0.6);
        vec2 toPart = pos - oc;
        float dd = length(toPart);
        if (dd < orad * 2.0) {
          vec2 push = normalize(toPart + 1e-4);
          vel += push * (orad * 2.0 - dd) * 3.0;
        }
      }

      pos += vel * 0.05;
      // Wrap.
      pos = mod(pos + 1.0, 2.0) - 1.0;
    }

    // Now pos is current. Step backwards for trail.
    vec2 trailPos = pos;
    float speedMag = length(vel);
    float speedNorm = clamp(speedMag * 3.0, 0.0, 1.0);
    // Particle color: red (slow) → orange → yellow → green → blue (fast).
    vec3 pcol;
    if (speedNorm < 0.5) {
      pcol = mix(vec3(1.0, 0.2, 0.1), vec3(1.0, 0.85, 0.2), speedNorm * 2.0);
    } else {
      pcol = mix(vec3(1.0, 0.85, 0.2), vec3(0.3, 0.5, 1.0), (speedNorm - 0.5) * 2.0);
    }

    // Trace trail backward.
    vec2 tpos = pos;
    vec2 tvel = vel;
    for (int s = 0; s < 18; s++) {
      // Re-curl from current trail position.
      vec2 cn = curl(tpos * 1.5 + vec2(uTime * 0.05, uTime * 0.03));
      tvel = cn * (0.5 + bass * 0.8);
      tpos = tpos - tvel * 0.05;
      tpos = mod(tpos + 1.0, 2.0) - 1.0;
      float segD = length(p - tpos);
      float intensity = (1.0 - float(s) / 18.0) * 0.6;
      float seg = exp(-segD * 200.0) * intensity;
      trailCol += pcol * seg;
      trailAccum += seg;
    }

    // Particle head — bright dot.
    float headD = length(p - pos);
    float head = exp(-headD * headD * 800.0);
    headCol += pcol * head * (0.7 + high * 0.6);
  }

  col += trailCol * 0.7;
  col += headCol;

  // --- 4. Speed heat overlay near obstacles ---
  float heatField = 0.0;
  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    vec2 c = obstPos[i] + vec2(
      sin(uTime * (0.3 + fi * 0.1) + fi) * 0.10,
      cos(uTime * (0.4 + fi * 0.07) + fi * 1.3) * 0.10
    ) * (0.4 + mid * 0.6);
    float radius = obstR[i] * (0.8 + 0.2 * sin(uTime * 1.5 + fi * 1.7)) * (0.7 + bass * 0.6);
    float d = length(p - c);
    if (d > radius && d < radius + 0.10) {
      heatField = max(heatField, exp(-(d - radius) * 20.0));
    }
  }
  col += vec3(0.3, 0.5, 1.0) * heatField * 0.2;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.3;

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeWindstreamBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.7);
  out[1] = 0.5 + 0.4 * Math.abs(Math.sin(t * 1.4));
  out[2] = 0.4 + 0.4 * Math.sin(t * 2.2 + 0.7);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.2 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.8 + 2.3);
  out[5] = 0.4 + 0.5 * Math.sin(t * 2.6 + 3.1);
  out[6] = 0.3 + 0.6 * Math.sin(t * 4.0 + 0.5);
  out[7] = 0.4 + 0.5 * Math.sin(t * 6.0 + 1.7);
  out[8] = 0.3 + 0.6 * Math.sin(t * 8.0 + 2.1);
}

export function computeWindstreamRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.012], [0.012, 0.040], [0.040, 0.080],
    [0.080, 0.180], [0.180, 0.300], [0.300, 0.460],
    [0.460, 0.640], [0.640, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0;
    let count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createWindstreamPipeline(gl: WebGL2RenderingContext): WindstreamPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    bands: new Float32Array(9),
  };
}

export function drawWindstream(
  gl: WebGL2RenderingContext,
  pipe: WindstreamPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}